import React, { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Property } from '@shared/schema';
import { fetchProperties } from '@/api-client';
import PropertyCard from '@/components/properties/PropertyCard';
import { Link, useLocation } from 'wouter'; // Assuming wouter is used for routing
import SEOHead from '@/components/seo/SEOHead';
import Breadcrumb from '@/components/seo/Breadcrumb';
import FAQ from '@/components/seo/FAQ';

const AllPropertiesPage = () => {
  const [location] = useLocation();

  // Extract search parameters from URL
  const searchParams = useMemo(() => {
    const params = new URLSearchParams(location.split('?')[1] || '');
    const filters: Record<string, any> = {};

    // Map URL parameters to filter object for Strapi
    if (params.has('city')) filters.city = params.get('city');
    if (params.has('propertyType')) filters.propertyType = params.get('propertyType');

    // Handle price range
    if (params.has('minPrice')) filters.minPrice = parseInt(params.get('minPrice') || '0', 10);
    if (params.has('maxPrice')) filters.maxPrice = parseInt(params.get('maxPrice') || '0', 10);

    // Handle other filters
    if (params.has('minRooms')) filters.minRooms = parseInt(params.get('minRooms') || '0', 10);
    if (params.has('minArea')) filters.minArea = parseInt(params.get('minArea') || '0', 10);
    if (params.has('yearBuilt')) filters.yearBuilt = parseInt(params.get('yearBuilt') || '0', 10);
    if (params.has('features')) filters.features = params.get('features');

    return filters;
  }, [location]);

  // Use the filters in the query
  const { data: properties, isLoading, error } = useQuery<Property[]>({
    queryKey: ['allProperties', searchParams], // Add searchParams to query key for proper cache invalidation
    queryFn: () => fetchProperties(searchParams),
  });

  // Generate dynamic SEO title based on active filters - MOVED BEFORE EARLY RETURNS
  const pageTitle = useMemo(() => {
    if (Object.keys(searchParams).length === 0) {
      return 'Alle Immobilien und Projekte | YADE HOME Köln';
    }

    const filterParts = [];
    if (searchParams.city) filterParts.push(searchParams.city);
    if (searchParams.propertyType) filterParts.push(searchParams.propertyType);

    return filterParts.length > 0
      ? `Immobilien ${filterParts.join(' ')} - YADE HOME`
      : 'Alle Immobilien - YADE HOME';
  }, [searchParams]);

  const faqItems = useMemo(() => [
    {
      question: "Wie finde ich die passende Immobilie?",
      answer: "Nutzen Sie unsere Suchfilter, um Immobilien nach Lage, Preis, Größe und Ausstattung zu filtern. Unsere Immobilienberater unterstützen Sie gerne persönlich bei der Suche nach Ihrer Traumimmobilie."
    },
    {
      question: "Kann ich Immobilienbesichtigungen online buchen?",
      answer: "Ja, Sie können direkt auf der Immobiliendetailseite einen Besichtigungstermin anfragen. Wir melden uns innerhalb von 24 Stunden bei Ihnen zur Terminbestätigung."
    },
    {
      question: "Werden die Immobilienangebote regelmäßig aktualisiert?",
      answer: "Ja, unsere Immobilienangebote werden täglich aktualisiert. Neue Objekte werden sofort veröffentlicht, verkaufte Immobilien zeitnah entfernt."
    },
    {
      question: "Bietet YADE Home auch Mietimmobilien an?",
      answer: "Aktuell konzentrieren wir uns auf den Verkauf von Immobilien, vor allem aus eigenen Projekten. Bei Interesse an Mietobjekten kontaktieren Sie uns gerne direkt."
    },
    {
      question: "Wie kann ich meine Suche speichern?",
      answer: "Erstellen Sie ein kostenloses Kundenkonto, um Immobilien auf Ihrer Merkliste zu speichern und einen Suchagenten einzurichten. Sie werden automatisch über passende neue Angebote informiert."
    }
  ], []);

  if (isLoading) {
    return (
      <div className="container mx-auto px-6 py-16">
        <h1 className="text-3xl md:text-4xl font-montserrat font-bold mb-8 text-center">Alle Immobilien</h1>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Loading skeletons */}
          {Array(6).fill(null).map((_, i) => (
            <div key={i} className="bg-white rounded-lg overflow-hidden shadow-lg animate-pulse">
              <div className="h-64 bg-gray-300"></div>
              <div className="p-6">
                <div className="h-7 bg-gray-300 rounded mb-4"></div>
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-10 bg-gray-300 rounded mt-6"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-16 text-center">
        <h1 className="text-3xl md:text-4xl font-montserrat font-bold mb-8">Alle Immobilien</h1>
        <p className="text-red-500">Es gab ein Problem beim Laden der Immobilien.</p>
      </div>
    );
  }

  return (
    <>
      <SEOHead
        title={pageTitle}
        description="Alle verfügbaren Immobilien und Projekte von YADE Home: Wohnungen und Häuser aus eigenen Neubauprojekten und im Maklerauftrag, in Köln und NRW."
        canonical="https://yade-home.de/immobilien"
        keywords="Immobilien Köln, Wohnungen kaufen Köln, Häuser Köln, Immobiliensuche, Immobilienkatalog, Neubau Köln, Neubauprojekte Köln, Eigentumswohnung Köln"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "RealEstateAgent",
          "name": "YADE HOME",
          "description": "Immobilien und Neubauprojekte in Köln und NRW",
          "url": "https://yade-home.de/immobilien",
          "areaServed": {
            "@type": "City",
            "name": "Köln"
          }
        }}
      />

      {/* Breadcrumb Navigation */}
      <Breadcrumb
        items={[
          { label: 'Immobilien', href: '/immobilien' }
        ]}
      />

      <div className="container mx-auto px-6 py-16">
        <h1 className="text-3xl md:text-4xl font-montserrat font-bold mb-8 text-center">Alle Immobilien</h1>
      {properties && properties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {properties.map((property) => (
            <PropertyCard key={property.id} property={property} />
          ))}
        </div>
      ) : (
        <div className="text-center">
          <p className="text-gray-500">Derzeit sind keine Immobilien verfügbar.</p>
        </div>
      )}
      </div>

      {/* FAQ Section */}
      <FAQ
        items={faqItems}
        title="Häufig gestellte Fragen zur Immobiliensuche"
        description="Hilfreiche Informationen zur Suche und Besichtigung von Immobilien"
      />
    </>
  );
};

export default AllPropertiesPage;
